<template>
  <app-layout>
    <v-container fluid>
      <v-card class="ma-3 pa-3">
        <v-card-title class="px-0">
          <v-layout row wrap align-center>
            <v-flex xs12 md8>
              <h1>Comunicaciones automáticas</h1>
            </v-flex>
            <v-flex xs12 md4 class="text-xs-right">
              <v-btn flat icon color="primary" :loading="loading" @click="fetchUserActions">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-card-title>

        <user-action-filters
          v-model="filters"
          :options="filterOptions"
          :loading="loading"
          @reset="resetFilters"
        ></user-action-filters>

        <user-actions-table
          :items="items"
          :loading="loading"
          :pagination.sync="pagination"
          @show-timeline="openTimeline"
        ></user-actions-table>
      </v-card>

      <user-action-timeline-dialog
        v-model="timelineDialog"
        :timeline="selectedTimeline"
        :loading="timelineLoading"
        :sending-action="manualSendingAction"
        :action-mode="manualActionMode"
        @open-preview="openPreview"
        @send-email="openManualSendConfirm"
        @mark-as-sent="openManualMarkConfirm"
      ></user-action-timeline-dialog>

      <v-dialog v-model="manualSendDialog" max-width="520px" persistent>
        <v-card>
          <v-card-title class="headline">{{ manualActionTitle }}</v-card-title>
          <v-card-text>
            <div class="body-1 mb-2">
              {{ manualActionMessage }}
              <strong>{{ selectedTimelineUserName }}</strong>
              .
            </div>
            <div v-if="manualSendStep" class="body-2 font-weight-medium">
              {{ manualSendStep.label }}
            </div>
            <div
              v-if="manualSendStep && manualSendStep.subject"
              class="caption grey--text text--darken-1 mt-1"
            >
              Asunto: {{ manualSendStep.subject }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              flat
              color="grey darken-1"
              :disabled="!!manualSendingAction"
              @click="cancelManualSend"
            >
              Cancelar
            </v-btn>
            <v-btn
              color="primary"
              :loading="!!manualSendingAction"
              :disabled="!manualSendStep"
              @click="confirmManualAction"
            >
              {{ manualActionButtonText }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
        {{ snackText }}
      </v-snackbar>
    </v-container>
  </app-layout>
</template>

<script>
import AppLayout from '@/views/layouts/AppLayout.vue';
import UserActionFilters from '../components/UserActionFilters.vue';
import UserActionsTable from '../components/UserActionsTable.vue';
import UserActionTimelineDialog from '../components/UserActionTimelineDialog.vue';
import UserActionsAPI from '../services/userActionsAPI.js';
import { OG_CONFIG } from '@/services/api/config.js';

const emptyFilters = {
  search: '',
  company: null,
  action: null,
  status: null,
  role: null,
  date_from: null,
  date_to: null,
};

export default {
  name: 'UserActionsPage',
  components: {
    AppLayout,
    UserActionFilters,
    UserActionsTable,
    UserActionTimelineDialog,
  },
  data() {
    return {
      items: [],
      filters: Object.assign({}, emptyFilters),
      filterOptions: {
        actions: [],
        companies: [],
        statuses: [],
        roles: [],
      },
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: '',
        totalItems: 0,
      },
      loading: false,
      timelineDialog: false,
      timelineLoading: false,
      selectedTimeline: null,
      manualSendDialog: false,
      manualSendStep: null,
      manualActionMode: null,
      manualSendingAction: null,
      snack: false,
      snackText: '',
      snackColor: 'info',
      fetchTimeout: null,
    };
  },
  computed: {
    requestKey() {
      return JSON.stringify({
        page: this.pagination.page,
        rowsPerPage: this.pagination.rowsPerPage,
        filters: this.filters,
      });
    },
    selectedTimelineUserName() {
      const user = this.selectedTimeline && this.selectedTimeline.user;
      return user && user.name ? user.name : 'este usuario';
    },
    manualActionTitle() {
      return this.manualActionMode === 'mark' ? 'Confirmar marcado' : 'Confirmar envío';
    },
    manualActionMessage() {
      return this.manualActionMode === 'mark'
        ? 'Vas a marcar como enviada esta comunicación para'
        : 'Vas a enviar manualmente esta comunicación a';
    },
    manualActionButtonText() {
      return this.manualActionMode === 'mark' ? 'Marcar como enviada' : 'Enviar email';
    },
  },
  mounted() {
    this.fetchUserActions();
  },
  methods: {
    fetchUserActions() {
      this.loading = true;
      UserActionsAPI.list(this.requestParams())
        .then(response => {
          this.items = response.data.data || [];
          this.filterOptions = Object.assign({}, this.filterOptions, response.data.filters || {});
          if (response.data.meta) {
            this.pagination.totalItems = Number(response.data.meta.total || 0);
            this.pagination.rowsPerPage = Number(
              response.data.meta.per_page || this.pagination.rowsPerPage,
            );
          }
        })
        .catch(() => {
          this.showSnack('error', 'No se han podido cargar las comunicaciones automaticas');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    requestParams() {
      const params = {
        page: this.pagination.page,
        per_page: this.pagination.rowsPerPage,
      };
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key] !== null && this.filters[key] !== '') {
          params[key] = this.filters[key];
        }
      });
      return params;
    },
    resetFilters() {
      this.filters = Object.assign({}, emptyFilters);
      this.pagination.page = 1;
    },
    openTimeline(item) {
      const userSnapshot = Object.assign({}, item);
      this.timelineDialog = true;
      this.timelineLoading = true;
      this.selectedTimeline = { user: userSnapshot, campaigns: {} };

      UserActionsAPI.show(item.id)
        .then(response => {
          this.selectedTimeline = {
            user: Object.assign({}, userSnapshot, response.data.user || {}),
            campaigns: response.data.campaigns || {},
          };
        })
        .catch(() => {
          this.showSnack('error', 'No se ha podido cargar el timeline');
          this.timelineDialog = false;
        })
        .finally(() => {
          this.timelineLoading = false;
        });
    },
    openManualSendConfirm(step) {
      if (this.manualSendingAction || !step || !step.action) return;
      this.manualSendStep = step;
      this.manualActionMode = 'send';
      this.manualSendDialog = true;
    },
    openManualMarkConfirm(step) {
      if (this.manualSendingAction || !step || !step.action) return;
      this.manualSendStep = step;
      this.manualActionMode = 'mark';
      this.manualSendDialog = true;
    },
    cancelManualSend() {
      if (this.manualSendingAction) return;
      this.manualSendDialog = false;
      this.manualSendStep = null;
      this.manualActionMode = null;
    },
    confirmManualAction() {
      if (!this.manualSendStep) return;
      if (this.manualActionMode === 'mark') {
        this.markManualEmailAsSent(this.manualSendStep);
      } else {
        this.sendManualEmail(this.manualSendStep);
      }
    },
    sendManualEmail(step) {
      const currentUser = this.selectedTimeline && this.selectedTimeline.user;
      if (this.manualSendingAction || !currentUser || !step || !step.action) return;

      this.manualSendingAction = step.action;
      this.manualActionMode = 'send';
      UserActionsAPI.send(currentUser.id, step.action)
        .then(() => {
          this.showSnack('success', 'Email enviado correctamente');
          return UserActionsAPI.show(currentUser.id).then(response => {
            this.selectedTimeline = {
              user: Object.assign({}, currentUser, response.data.user || {}),
              campaigns: response.data.campaigns || {},
            };
            this.fetchUserActions();
          });
        })
        .catch(error => {
          this.showSnack('error', this.errorMessage(error, 'No se ha podido enviar el email'));
        })
        .finally(() => {
          this.manualSendingAction = null;
          this.manualSendDialog = false;
          this.manualSendStep = null;
          this.manualActionMode = null;
        });
    },
    markManualEmailAsSent(step) {
      const currentUser = this.selectedTimeline && this.selectedTimeline.user;
      if (this.manualSendingAction || !currentUser || !step || !step.action) return;

      this.manualSendingAction = step.action;
      this.manualActionMode = 'mark';
      UserActionsAPI.markAsSent(currentUser.id, step.action)
        .then(() => {
          this.showSnack('success', 'Comunicación marcada como enviada');
          return UserActionsAPI.show(currentUser.id).then(response => {
            this.selectedTimeline = {
              user: Object.assign({}, currentUser, response.data.user || {}),
              campaigns: response.data.campaigns || {},
            };
            this.fetchUserActions();
          });
        })
        .catch(error => {
          this.showSnack(
            'error',
            this.errorMessage(error, 'No se ha podido marcar la comunicación'),
          );
        })
        .finally(() => {
          this.manualSendingAction = null;
          this.manualSendDialog = false;
          this.manualSendStep = null;
          this.manualActionMode = null;
        });
    },
    showSnack(color, text) {
      this.snackColor = color;
      this.snackText = text;
      this.snack = true;
    },
    errorMessage(error, fallback) {
      return error && error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : fallback;
    },
    openPreview(url) {
      window.open(this.absolutePreviewUrl(url), '_blank');
    },
    absolutePreviewUrl(url) {
      if (!url) return null;
      if (url.indexOf('http') === 0) return url;
      return `${OG_CONFIG.BASE_URL}${url}`;
    },
  },
  watch: {
    requestKey() {
      clearTimeout(this.fetchTimeout);
      this.fetchTimeout = setTimeout(() => {
        this.fetchUserActions();
      }, 300);
    },
    filters: {
      deep: true,
      handler() {
        if (this.pagination.page !== 1) {
          this.pagination.page = 1;
        }
      },
    },
  },
};
</script>
